'use client'

import Link from 'next/link'
import { useParams } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

import { useGetCatalogBrandsCategoriesQuery } from '@/entities/catalog-brands/api/catalogBrandsApi'

import { SvgArrowDown } from '@/shared/lib/@deprecated/iport-icons/iconsA/SvgArrowDown'

import styles from './brand-categories-list.module.scss'

// Максимальное количество отображаемых категорий до кнопки "Показать еще"
const MAX_CATEGORIES = 7

// Функция преобразования объекта категорий в массив для удобного рендеринга
const transformCategoriesToArray = (categories: Record<string, { NAME: string; COUNT: number }> = {}) => {
	return Object.keys(categories).map(key => ({
		NAME: categories[key].NAME,
		COUNT: categories[key].COUNT,
		LINK: key
	}))
}

interface BrandCategoriesListProps {
	className?: string
}

/**
 * Компонент списка категорий бренда
 */
export const BrandCategoriesList: React.FC<BrandCategoriesListProps> = ({ className }) => {
	const params = useParams()
	const [isOpen, setIsOpen] = useState(true)
	const [isSlicedList, setIsSlicedList] = useState(true)

	// Создаем собственный аккордеон по аналогии с компонентом в проекте
	const contentRef = useRef<HTMLDivElement>(null)

	// Получаем код бренда из URL
	const brandCode = params.brand as string

	// Получаем категории бренда с помощью RTK Query
	const { data: categories, isLoading } = useGetCatalogBrandsCategoriesQuery(
		{ brandCode },
		{ skip: !brandCode || brandCode === `[brand]` }
	)

	// Преобразуем объект категорий в массив для удобного рендеринга
	const categoriesList = transformCategoriesToArray(categories)

	// Сохраняем последний загруженный список категорий для использования во время загрузки
	const [cachedCategories, setCachedCategories] = useState([])

	// Обновляем кэш, когда данные загружены
	useEffect(() => {
		// Используем JSON.stringify для проверки реального изменения данных
		// чтобы избежать бесконечного цикла обновлений
		if (!isLoading && categories && Object.keys(categories).length > 0) {
			const newCategoriesList = transformCategoriesToArray(categories)
			setCachedCategories(newCategoriesList)
		}
	}, [isLoading, categories])

	// Используем кэш, если текущие данные загружаются или пусты
	const displayCategories = categoriesList.length > 0 ? categoriesList : cachedCategories

	// Если категорий нет даже в кэше, не показываем компонент
	if (!displayCategories.length) {
		return null
	}

	const handleToggle = () => {
		setIsOpen(!isOpen)
	}

	// Обработчик кнопки "Показать еще"
	const handleShowMore = () => {
		setIsSlicedList(false)
	}

	// Обработчик кнопки "Скрыть"
	const handleHide = () => {
		setIsSlicedList(true)
	}

	// Проверка, нужно ли показывать кнопку "Показать еще" для отображаемых категорий
	const hasMoreCategories = displayCategories.length > MAX_CATEGORIES

	const categoryParam = params.category
	const activeCategory = categoryParam ? (Array.isArray(categoryParam) ? categoryParam[0] : categoryParam) : ``

	return (
		<div className={`${styles.categoriesList} ${className || ''}`}>
			<div className={styles.accordion}>
				{/* Заголовок аккордеона */}
				<div className={styles.header} onClick={handleToggle}>
					<span className={styles.title}>Тип товара</span>
					<SvgArrowDown size={24} className={`${styles.icon} ${isOpen ? styles.iconOpen : ''}`} />
				</div>
				{/* Содержимое аккордеона */}
				<div className={styles.contentWrapper} aria-expanded={isOpen}>
					<div ref={contentRef} className={styles.content}>
						<ul className={styles.list}>
							{displayCategories
								.slice(0, isSlicedList ? MAX_CATEGORIES : displayCategories.length)
								.map((item, index) => (
									<li
										key={item.LINK}
										className={`${styles.item} ${activeCategory === item.LINK ? styles.active : ''} ${
											!isSlicedList && index >= MAX_CATEGORIES ? styles.expanded : ''
										}`}>
										<Link prefetch={false} href={`/brand/${brandCode}/${item.LINK}`}>
											<span className={styles.name}>{item.NAME}</span>
											<span className={styles.count}>{item.COUNT}</span>
										</Link>
									</li>
								))}
						</ul>

						{hasMoreCategories && (
							<div className={styles.showMoreWrapper} data-sliced={isSlicedList ? 'true' : 'false'}>
								{/* Обе кнопки всегда в DOM, но скрываются через CSS */}
								<button
									className={`${styles.showMoreButton} ${!isSlicedList ? styles.hidden : ''}`}
									onClick={handleShowMore}
									data-visible={isSlicedList}>
									Показать еще
								</button>
								<button
									className={`${styles.hideButton} ${isSlicedList ? styles.hidden : ''}`}
									onClick={handleHide}
									data-visible={!isSlicedList}>
									Скрыть
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
